import { Link, useParams } from "react-router-dom";
import {
  Sparkles, Flower2, Moon, Droplet, Leaf, Wind, Shield, HeartPulse, Compass,
  BookHeart, Sun, Baby, CheckCircle2, ArrowRight, MessageCircle, CalendarHeart,
} from "lucide-react";
import { TREATMENTS, SITE } from "@/lib/site";
import { useSEO } from "@/lib/seo";

const iconMap = { Sparkles, Flower2, Moon, Droplet, Leaf, Wind, Shield, HeartPulse, Compass, BookHeart, Sun, Baby };

// Rich content per treatment
const CONTENT = {
  "infertility": {
    tagline: "Restore fertility, naturally.",
    overview: "Infertility affects nearly 1 in 6 couples. In Ayurveda, we address the four pillars — Rutu (timing), Kshetra (uterus), Ambu (nutrition) and Beeja (egg & sperm quality) — to create the ideal environment for conception.",
    approach: ["Detailed Ayurvedic + modern diagnostic assessment", "3–6 month personalized fertility program", "Uttarbasti and Panchakarma when indicated", "Nutrition, yoga and stress management support", "Partner protocols for male factor"],
    conditions: ["Unexplained infertility", "PCOS-related", "Low AMH / poor ovarian reserve", "Endometriosis", "Tubal blockage", "Male factor"],
  },
  "pcos": {
    tagline: "Balance your hormones, reclaim your cycle.",
    overview: "PCOS is far more than irregular periods — it is a whole-body metabolic and hormonal imbalance. Our multi-layered protocol addresses the root: Ama, Agni and the interplay of Kapha and Vata doshas.",
    approach: ["Diet plan customized to your prakriti", "Classical herbs (Kanchnar Guggulu, Shatavari, Ashokarishta and more)", "Panchakarma detox when required", "Lifestyle, yoga and sleep hygiene", "Regular monitoring of cycles, weight and labs"],
    conditions: ["Irregular / missed periods", "Weight gain, insulin resistance", "Acne and hirsutism", "PCOS-related infertility", "Mood swings and PMS"],
  },
  "irregular-periods": {
    tagline: "Rebuild a healthy monthly rhythm.",
    overview: "Cycles longer than 35 days, shorter than 21, or wildly unpredictable are your body's signal that something is off. We identify the root cause — hormonal, nutritional, thyroid, PCOS or stress — and treat it with precision.",
    approach: ["Comprehensive assessment and lab review", "Cycle regulation herbs and diet", "Lifestyle recalibration", "Follow-up every cycle for 3–4 months"],
    conditions: ["Oligomenorrhea", "Polymenorrhea", "Amenorrhea", "Post-pill irregularity"],
  },
  "menstrual-disorders": {
    tagline: "Painful, heavy or missed? Let's fix it.",
    overview: "Menstrual disorders — heavy bleeding, severe pain, clots, PMS — are treatable. In Ayurveda, most respond beautifully to targeted internal medicine and lifestyle changes within 3 cycles.",
    approach: ["Ayurvedic diagnosis of dosha involvement", "Herbal formulations for bleeding, pain, PMS", "Diet and daily routine reset", "Basti therapy when required"],
    conditions: ["Menorrhagia (heavy flow)", "Dysmenorrhea (painful periods)", "PMS / PMDD", "Clots and irregular bleeding"],
  },
  "low-amh": {
    tagline: "Support ovarian reserve at any age.",
    overview: "A low AMH result is not the end of the road. Rasayana therapy, targeted herbs and Uttarbasti can meaningfully improve egg quality even when quantity is limited.",
    approach: ["90-day Rasayana protocol", "Shatavari, Ashwagandha and Kumari", "Uttarbasti in cycle", "Antioxidant-rich diet, sleep and stress support", "Coordinated care with IVF specialists if needed"],
    conditions: ["Low AMH", "Poor ovarian reserve", "Advanced maternal age fertility", "Pre-IVF preparation"],
  },
  "tubal-blockage": {
    tagline: "Open the path — naturally.",
    overview: "Fallopian tube blockage prevents fertilization. Uttarbasti, a specialized Panchakarma procedure, has shown remarkable results in restoring tubal patency when combined with oral medicine.",
    approach: ["HSG report review", "Uttarbasti course under sterile conditions", "Oral internal medicine", "Diet and lifestyle for reproductive health"],
    conditions: ["Unilateral blockage", "Bilateral blockage (case-by-case)", "Post-infection or endometriosis-related"],
  },
  "endometriosis": {
    tagline: "Move beyond monthly pain.",
    overview: "Endometriosis is inflammatory. Ayurveda addresses the underlying Rakta-Pitta dushti with cooling, anti-inflammatory herbs and detoxification — reducing pain and slowing progression.",
    approach: ["Pain-relief herbs (no painkillers)", "Panchakarma for deep detox", "Anti-inflammatory diet plan", "Fertility support if desired"],
    conditions: ["Painful periods", "Chocolate cysts", "Endometriosis-related infertility", "Chronic pelvic pain"],
  },
  "pregnancy-care": {
    tagline: "A month-by-month care plan.",
    overview: "Garbhini Paricharya — classical Ayurvedic prenatal care — offers month-specific foods, herbs and routines to nourish mother and baby. We combine this with modern antenatal monitoring for the best of both worlds.",
    approach: ["Trimester-wise diet & recipe plan", "Safe herbs for common concerns", "Yoga and pranayama for pregnancy", "Preparation for labor and delivery"],
    conditions: ["First trimester nausea", "Anemia in pregnancy", "Gestational diabetes support", "High-risk pregnancy co-care"],
  },
  "preconception-counselling": {
    tagline: "The healthiest babies begin here.",
    overview: "A structured 3–6 month preconception program purifies both partners and prepares body and mind for a healthy conception. Highly recommended after miscarriage, IVF failure or advanced age.",
    approach: ["Gentle Panchakarma", "Nutrition and antioxidant program", "Mind-body work: meditation, yoga", "Partner protocols and testing"],
    conditions: ["Pre-marriage counselling", "Post-miscarriage recovery", "IVF preparation", "Advanced maternal age"],
  },
  "garbha-sanskar": {
    tagline: "Nurture your baby's mind, body and soul.",
    overview: "Garbha Sanskar is the sacred science of shaping your baby's consciousness during pregnancy. Through music, mantras, meditation, reading and mindful living, you build the foundation of a calm, intelligent and healthy child.",
    approach: ["Weekly Garbha Sanskar sessions", "Curated music, mantra and story kit", "Emotional wellness for the mother", "Nutritional and herbal support"],
    conditions: ["All pregnancies", "IVF pregnancies", "High-anxiety mothers", "Second-time mothers"],
  },
  "menopause-care": {
    tagline: "Transition with grace and vitality.",
    overview: "Menopause need not mean suffering. Ayurveda offers gentle, HRT-free care for hot flashes, mood swings, sleep issues, bone and heart health.",
    approach: ["Rasayana herbs (Shatavari, Ashwagandha)", "Diet and lifestyle for the transition", "Bone and heart health protocol", "Emotional wellness support"],
    conditions: ["Perimenopause symptoms", "Post-menopausal wellness", "Bone density support", "Menopausal weight management"],
  },
  "post-delivery-care": {
    tagline: "Sutika Paricharya — recovery for a lifetime.",
    overview: "The first 42 days after delivery shape your health for decades. Sutika Paricharya provides warm oil massage, herbal decoctions, restorative diet and lactation support — restoring the mother completely.",
    approach: ["Daily abhyanga and steam", "Herbal decoctions and diet plan", "Lactation support", "Postpartum yoga and mental wellness"],
    conditions: ["Vaginal or C-section recovery", "Low milk supply", "Postpartum depression risk", "Weight and abdomen recovery"],
  },
};

export default function TreatmentDetail() {
  const { slug } = useParams();
  const treatment = TREATMENTS.find((t) => t.slug === slug);
  const content = CONTENT[slug];

  useSEO({
    title: treatment ? treatment.title : "Treatment",
    description: content ? content.overview.slice(0, 155) : "Ayurvedic treatment at Nari Arogya, Pune.",
    path: `/treatments/${slug}`,
  });

  if (!treatment || !content) {
    return (
      <section className="pt-40 pb-20 na-container text-center">
        <h1 className="text-4xl">Treatment not found</h1>
        <Link to="/treatments" className="btn-primary mt-6">See all treatments</Link>
      </section>
    );
  }

  const Icon = iconMap[treatment.icon] || Sparkles;
  const waLink = `https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent(`Hello Dr. Anita, I'd like to know more about ${treatment.title}.`)}`;

  return (
    <>
      <section className="pt-32 pb-16 lotus-bg" data-testid={`treatment-hero-${slug}`}>
        <div className="na-container grid gap-10 lg:grid-cols-[auto_1fr] items-center">
          <div className="w-24 h-24 rounded-3xl bg-[color:var(--na-teal)]/10 text-[color:var(--na-teal)] flex items-center justify-center">
            <Icon size={40} />
          </div>
          <div>
            <span className="eyebrow">Treatment</span>
            <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">{treatment.title}</h1>
            <p className="mt-3 text-lg text-[color:var(--na-maroon)] italic font-display">{content.tagline}</p>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="na-container grid gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-10">
            <div>
              <h2 className="text-3xl">Overview</h2>
              <p className="mt-4 text-[color:var(--na-muted)] leading-relaxed text-base">{content.overview}</p>
            </div>

            <div>
              <h2 className="text-3xl">Our Approach</h2>
              <ul className="mt-4 space-y-3">
                {content.approach.map((a) => (
                  <li key={a} className="flex gap-3 items-start">
                    <CheckCircle2 size={20} className="text-[color:var(--na-teal)] shrink-0 mt-0.5" />
                    <span className="text-[color:var(--na-muted)]">{a}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h2 className="text-3xl">Conditions Treated</h2>
              <div className="mt-4 flex flex-wrap gap-2">
                {content.conditions.map((c) => (
                  <span key={c} className="px-4 py-2 rounded-full bg-[color:var(--na-section)] border border-[color:var(--na-border)] text-sm">
                    {c}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <aside className="na-card p-7 h-fit sticky top-28">
            <h3 className="text-2xl">Book a consultation</h3>
            <p className="mt-2 text-sm text-[color:var(--na-muted)]">Every plan is personalized. Start with an assessment.</p>
            <div className="mt-5 flex flex-col gap-3">
              <Link to="/book" className="btn-primary justify-center" data-testid="treatment-book-btn">
                <CalendarHeart size={16} /> Book Appointment
              </Link>
              <a href={waLink} target="_blank" rel="noreferrer" className="btn-outline justify-center">
                <MessageCircle size={16} /> WhatsApp Now
              </a>
            </div>
            <div className="mt-6 pt-6 border-t border-[color:var(--na-border)]">
              <p className="text-xs uppercase tracking-widest text-[color:var(--na-muted)] font-semibold">Related</p>
              <ul className="mt-3 space-y-2">
                {TREATMENTS.filter((t) => t.slug !== slug).slice(0, 4).map((t) => (
                  <li key={t.slug}>
                    <Link to={`/treatments/${t.slug}`} className="text-sm hover:text-[color:var(--na-teal)] inline-flex items-center gap-1.5">
                      <ArrowRight size={12} /> {t.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}
